import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE, routingState } from '@adornis/router/client/routing-state.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { LastVisitedAPI } from '@chancenwerk/campus-learning-content/api/LastVisitedAPI.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLessonDiagnostic, LearningDataAPI } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { chancenKurveAPI } from '@chancenwerk/campus-learning-content/api/chancenKurveAPI.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea, CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-chip.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-icon.js';
import { ChromiumSidebar } from '@chancenwerk/design/components/cr-sidebar.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, of, switchMap, takeUntil, tap } from 'rxjs';
import '../components/campus-component-attachements.js';
import '../components/exercises/campus-component-exercise-chemistry-formula.js';
import '../components/exercises/campus-component-exercise-geogebra.js';
import '../components/exercises/campus-component-exercise-h5p.js';
import '../components/exercises/campus-component-exercise-levumi-german.js';
import '../components/exercises/campus-component-exercise-levumi-math.js';
import '../components/exercises/campus-component-exercise-levumi-number-dictate.js';
import '../components/exercises/campus-component-exercise-serlo-editor.js';
import '../components/exercises/campus-component-exercise-serlo.js';
import '../components/exercises/campus-component-exercise-text.js';
import { scrollToTop } from '../globals.js';
import { CampusRoutes } from './../routes.js';

@customElement('campus-page-play-lesson')
export class CampusPagePlayLesson extends ChemistryLitElement {
  @property() lessonID = new RXController(this, '');

  @state() private readonly _lesson = new RXController(
    this,
    this.lessonID.observable.pipe(
      filter(Boolean),
      switchMap(id => {
        if (!id.length) return of(new CampusLesson({}));
        else return subscribeByID(CampusLesson, id)(CampusLesson.allFields);
      }),
    ),
  );

  @state() private readonly _section = new RXController(
    this,
    this.lessonID.observable.pipe(
      switchMap(id => LearningContentAPI.getSectionForLesson(id ?? '', true)(CampusSection.allFields)),
    ),
  );

  @state() private readonly _exercises = new RXController(
    this,
    this._lesson.observable.pipe(
      switchMap(lesson => {
        if (!lesson?.exerciseIDs) return of([]);
        else return subscribeByID(CampusExercise, lesson.exerciseIDs)(CampusExercise.allFields);
      }),
    ),
  );

  @state() private readonly _lessonDiagnostic = new RXController(
    this,
    combineLatest([currentUser(), this._section.observable, this.lessonID.observable]).pipe(
      filter(Boolean),
      switchMap(
        ([user, section, lessonId]) =>
          LearningDataAPI.getDiagnosticForLessons(
            user?._id || 'NOT_LOGGED_IN',
            section?.lessonIDs?.filter(id => id !== lessonId) ?? [],
          )(CampusLessonDiagnostic.allFields) as Promise<CampusLessonDiagnostic[]>,
      ),
    ),
  );

  @state() private readonly _solvedExercises = new RXController(
    this,
    combineLatest([currentUser(), this._section.observable, this.lessonID.observable]).pipe(
      filter(Boolean),
      switchMap(
        ([user, section, lessonId]) =>
          LearningDataAPI.getSolvedExercisesForLesson(lessonId ?? '')() as Promise<string[]>,
      ),
    ),
  );

  @state() private readonly _nextLesson = new RXController<undefined | CampusLesson>(this, undefined);
  @state() private readonly _previousLesson = new RXController<undefined | CampusLesson>(this, undefined);
  @state() private readonly _lastFinish = new RXController(this, DateTime.now().minus({ years: 10 }));

  @state() _ = new RXController(this, routingState.searchParams.get('qrCode'));

  size = new WindowSizeController(this);

  public override connectedCallback(): void {
    super.connectedCallback();

    ChromiumSidebar.close();

    combineLatest([this._lesson.observable, this._section.observable])
      .pipe(
        takeUntil(this.disconnected),
        filter(Boolean),
        tap(([lesson, section]) => {
          LastVisitedAPI.commit(lesson?._id ?? '')().catch(console.error);

          if (lesson && this._isChancenKurveLesson()) {
            chancenKurveAPI
              .lastFinish(lesson._id, (Math.random() * 50000000).toFixed())()
              .then(date => {
                if (date.isValid) {
                  this._lastFinish.next(date);
                }
              });
          }

          if (!lesson || !section) {
            this._nextLesson.next(undefined);
            this._previousLesson.next(undefined);
            return;
          }

          const lessonIDs = this._section.value?.lessonIDs ?? [];
          const indexOfCurrentLesson = lessonIDs.findIndex(e => e === this.lessonID.value);

          if (indexOfCurrentLesson === 0) this._previousLesson.next(undefined);
          else this._previousLesson.next(section.lessons?.at(indexOfCurrentLesson - 1));

          if (indexOfCurrentLesson === lessonIDs.length - 1) this._nextLesson.next(undefined);
          else this._nextLesson.next(section.lessons?.at(indexOfCurrentLesson + 1));
        }),
      )
      .subscribe();

    // goTo({ path: NO_CHANGE, searchParams: new URLSearchParams({ qrCode: 'false' }) });
  }

  public override disconnectedCallback(): void {
    ChromiumSidebar.open();
    super.disconnectedCallback();
  }

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_CONTENT, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _isChancenKurveLesson() {
    return this._section.value?.name.startsWith('CHANCENkurve');
  }

  private _isChancenCorner() {
    return this._section.value?.area === CampusArea.EMPLOYEES;
  }

  private _isOnlyLevumi() {
    return this._exercises.value?.every(
      e =>
        e.type === ExerciseType.LEVUMIMATH ||
        e.type === ExerciseType.LEVUMIGERMAN ||
        e.type === ExerciseType.LEVUMI_NUMBER_DICTATE,
    );
  }

  private _content() {
    return html`
      <x-flex
        ${css({
          maxWidth: '3.2em',
          position: 'sticky',
          zIndex: '1001',
          top: '1.5em',
          left: '1.5em',
        })}
        space="sm"
      >
        ${this.size.mobile
          ? nothing
          : ChromiumSidebar.isOpen()
          ? html`
              <cr-button
                class=""
                mode="fab"
                icon="close"
                ${css({
                  color: 'white',
                  background: this.colors.primary,
                  backgroundImage: `linear-gradient(to left, ${this.colors.primary} 0%, ${this.colors.primary} 25%, ${this.colors.primary} 75%, ${this.colors.primary} 100%)`,
                })}
                @click=${() => {
                  ChromiumSidebar.close();
                  this.requestUpdate();
                }}
              ></cr-button>
            `
          : html` <cr-button
              mode="fab"
              icon="reply"
              ${css({
                color: 'white',
                background: this.colors.primary,
                backgroundImage: `linear-gradient(to left, ${this.colors.primary} 0%, ${this.colors.primary} 25%, ${this.colors.primary} 75%, ${this.colors.primary} 100%)`,
              })}
              @click=${async () => this._close()}
            ></cr-button>`}
      </x-flex>
      <cr-section ${css({ maxWidth: '80%' })}>
        <cr-flex space="xxl" padding="md">
          <cr-flex space="md" horizontal space-between center crossaxis-center>
            <cr-h2>Lektion ${this._lesson.value?.name}</cr-h2>
            <cr-icon @click=${async () => this._close()} ${css({ cursor: 'pointer' })}> close </cr-icon>
          </cr-flex>
          ${this._playSection()}
        </cr-flex>
      </cr-section>
    `;
  }

  private _playSection() {
    if (this._isChancenKurveLesson() && Math.abs(this._lastFinish.value?.diff(DateTime.now(), 'days').days ?? 1) < 1) {
      return html`<cr-flex crossaxis-center space="lg">
        <cr-icon ${css({ fontSize: '2rem' })}>warning</cr-icon>
        <cr-text bold>
          Du hast diese Lektion heute schon abgeschlossen. Lektionen der CHANCENkurve können nur einmal am Tag
          abgeschlossen werden!
        </cr-text>
      </cr-flex>`;
    }

    return html` ${this._section.isLoading || this._lesson.isLoading || this._lessonDiagnostic.isLoading
      ? html`<cr-campus-loader></cr-campus-loader>`
      : html`
          <cr-flex space="xxl">
            ${repeat(
              this._exercises.value?.sort((a, b) => a.sortingOrderInLesson - b.sortingOrderInLesson) ?? [],
              (exercise, index) => {
                if (exercise.title?.length)
                  return html`<cr-flex space="md">
                    <cr-card padding="lg">
                      <cr-flex horizontal space-between crossaxis-center space="md">
                        <cr-text ${css({ fontSize: '1.5rem', lineHeight: '1.5rem' })} bold>${exercise.title}</cr-text>
                        <cr-flex horizontal space="md" crossaxis-center>
                          ${this._solvedExercises.value?.includes(exercise._id)
                            ? html`
                                <cr-chip tone="ascent" ${css({ color: 'white' })}>
                                  <cr-text>Bereits einmal gelöst</cr-text>
                                </cr-chip>
                              `
                            : html`<cr-icon ${css({ fontSize: '1.2rem', opacity: '0.5' })}>edit</cr-icon>`}
                        </cr-flex>
                      </cr-flex>
                    </cr-card>

                    ${this._showExercise(exercise)}
                  </cr-flex>`;
                return html`${this._showExercise(exercise)}`;
              },
            )}
            <campus-component-attachements
              showDownload
              .fileIDs=${this._lesson.value?.attachementIDs ?? []}
            ></campus-component-attachements>
          </cr-flex>
          <cr-flex horizontal space-between> </cr-flex>
          <cr-grid columns="3">
            <cr-flex horizontal start>
              ${this._previousLesson.value && !this._isOnlyLevumi()
                ? html`
                    <cr-button tone="submit" @click=${() => this._routeToNeighboringLesson(false)}> Zurück </cr-button>
                  `
                : html`<div></div>`}
            </cr-flex>
            ${this._isChancenKurveLesson()
              ? html`<cr-flex></cr-flex>`
              : html`<cr-flex horizontal center>
                  <cr-button tone="submit" @click=${async () => this._close(true)}> Fertig! </cr-button>
                </cr-flex>`}
            <cr-flex horizontal end>
              ${this._nextLesson.value && !this._isOnlyLevumi()
                ? html`
                    <cr-button tone="submit" @click=${() => this._routeToNeighboringLesson(true)}> Weiter </cr-button>
                  `
                : html`<div></div>`}
            </cr-flex>
          </cr-grid>
        `}`;
  }

  private async _routeToNeighboringLesson(forward: boolean) {
    if (forward) {
      if (this._nextLesson.value) {
        const violatedDependency = this._checkLessonEntry(this._nextLesson.value);
        if (violatedDependency) {
          await ChromiumDialog.alert(
            'Du kannst das nächste Quiz noch nicht betreten, dir fehlt folgende Voraussetzung: ' + violatedDependency,
          );
          return;
        }
        goTo(CampusRoutes.play + '/' + this._nextLesson.value._id);
        setTimeout(() => scrollToTop.next(true), 250);
      } else {
        if (
          await ChromiumDialog.confirm('Du bist Fertig mit diesem Themenbereich! Zurück zur Übersicht?', {
            acceptText: 'Bring mich zurück!',
            declineText: 'Bleib hier!',
          })
        ) {
          goTo(CampusRoutes.dashboard);
        }
      }
    } else {
      if (!this._previousLesson.value) return;
      const violatedDependency = this._checkLessonEntry(this._previousLesson.value);
      if (violatedDependency) {
        await ChromiumDialog.alert(
          'Du kannst das nächste Quiz noch nicht betreten, dir fehlt folgende Voraussetzung: ' + violatedDependency,
        );
        return;
      }
      goTo(CampusRoutes.play + '/' + this._previousLesson.value._id);
      setTimeout(() => scrollToTop.next(true), 250);
    }
  }

  private _checkLessonEntry(lesson: CampusLesson): Maybe<string> {
    const diagnosticForLesson = this._lessonDiagnostic.value?.find(dia => dia.lessonID === lesson._id);
    if (!diagnosticForLesson) return undefined;
    for (let i = 0; i < diagnosticForLesson.dependingOnLesson.length; i++) {
      if ((diagnosticForLesson.dependingOnLesson[i]?.ratio ?? 0) < 0.5)
        return diagnosticForLesson.dependingOnLesson[i]?.name;
    }
    return undefined;
  }

  private _showExercise(exercise: CampusExercise) {
    switch (exercise.type) {
      case ExerciseType.H5P:
        return html`
          <campus-component-exercise-h5p
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%' })}
          ></campus-component-exercise-h5p>
        `;
      case ExerciseType.FREITEXT:
        return html`
          <campus-component-exercise-text
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            .mode="${'play'}"
            ${css({ width: '100%' })}
          ></campus-component-exercise-text>
        `;
      case ExerciseType.GEOGEBRA:
        return html`
          <campus-component-exercise-geogebra
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%' })}
          ></campus-component-exercise-geogebra>
        `;
      case ExerciseType.LEVUMIGERMAN:
        return html`
          <campus-component-exercise-levumi-german
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%', height: '100%' })}
          ></campus-component-exercise-levumi-german>
        `;
      case ExerciseType.LEVUMIMATH:
        return html`
          <campus-component-exercise-levumi-math
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%', height: '100%' })}
          ></campus-component-exercise-levumi-math>
        `;
      case ExerciseType.LEVUMI_NUMBER_DICTATE:
        return html`
          <campus-component-exercise-levumi-number-dictate
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%', height: '100%' })}
          ></campus-component-exercise-levumi-number-dictate>
        `;
      case ExerciseType.SERLO:
        return html`
          <campus-component-exercise-serlo
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%' })}
          ></campus-component-exercise-serlo>
        `;
      case ExerciseType.SERLO_EDITOR:
        return html`
          <campus-component-exercise-serlo-editor
            .exerciseID="${exercise._id}"
            .lessonID="${this._lesson.value?._id}"
            ${css({ width: '100%' })}
          ></campus-component-exercise-serlo-editor>
        `;
      case ExerciseType.CHEMISTRY_FORMULA:
        return html`
          <cr-flex crossaxis-center>
            <campus-component-exercise-chemistry-formula
              .exerciseID="${exercise._id}"
              .lessonID="${this._lesson.value?._id}"
              ${css({ width: '100%', maxWidth: '500px' })}
            ></campus-component-exercise-chemistry-formula>
          </cr-flex>
        `;
    }
  }

  private async _close(done?: boolean) {
    if (done && this._isChancenKurveLesson()) {
      await chancenKurveAPI.commitFinish(this.lessonID.value ?? '')();
      this.lessonID.next(this.lessonID.value);
    }

    if (done && this._isChancenCorner()) {
      await LearningDataAPI.setCornerFinished(this.lessonID.value ?? '')();
    }

    // Go Back twice to override the qr-code eventlog proxy
    // ! Maybe not needed anymore
    // const internalRouting = Cookies.get('internalRouting');
    // if (internalRouting && internalRouting === 'true') {
    //   Cookies.set('internalRouting', 'false');
    //   history.back();
    //   history.back();
    // } else {
    //   goTo(CampusRoutes.allLessons);
    // }

    goTo(CampusRoutes.allLessons);
  }
  override styles() {
    return [
      {
        '@media screen and (max-width: 425px)': {
          ':host cr-section > cr-flex': {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    ];
  }
}
